import { z } from 'zod';
import type { Result } from './result';
import { toContractError, type ContractError } from './errors';
import { UuidSchema } from './uuid';

const AuthClientSchema = z.object({
  type: z.literal('auth'),
  token: z.string(),
  trace_id: UuidSchema,
  session_id: UuidSchema.optional(),
  robot_id: UuidSchema.optional(),
  lease_token: z.string().optional(),
});

const AuthStatusSchema = z.object({
  type: z.literal('auth'),
  status: z.union([z.literal('ok'), z.literal('error')]),
});

const AuthErrorSchema = z.object({
  error: z.string(),
});

const WsPingClientSchema = z.object({
  type: z.literal('ws_ping'),
  trace_id: UuidSchema.optional(),
});

const WsPongSchema = z.object({
  type: z.literal('ws_pong'),
});

const MotionIntentClientSchema = z
  .object({
    handler: z.literal('gamepad'),
    type: z.literal('motion_intent'),
    session_id: UuidSchema,
    robot_id: UuidSchema,
    seq: z.number().int().nonnegative(),
    ts_ms: z.number().int().nonnegative(),
    horizon_ms: z.number().int().min(100).max(500),
    axes: z.tuple([z.number(), z.number(), z.number()]),
  })
  .passthrough();

const GrabCommandSchema = z
  .object({
    handler: z.literal('gamepad'),
    type: z.literal('grab'),
    session_id: UuidSchema,
    robot_id: UuidSchema,
    message_id: UuidSchema,
    idempotency_key: z.string().min(1),
  })
  .passthrough();

const BotSummarySchema = z
  .object({
    id: z.string(),
    name: z.string().optional().nullable(),
    type: z.string().optional().nullable(),
    status: z.string().optional().nullable(),
    image: z.string().optional().nullable(),
    telemetry: z.record(z.unknown()).optional().nullable(),
    teleop_status: z.record(z.unknown()).optional().nullable(),
    teleop_status_at: z.string().optional().nullable(),
    channel: z.string().optional().nullable(),
  })
  .passthrough();

const BotRegisterDataSchema = z.object({
  id: z.string().optional().nullable(),
  robot_id: UuidSchema.optional(),
  robot_uuid: UuidSchema.optional(),
  session_id: UuidSchema.optional(),
  name: z.string().optional().nullable(),
  type: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  telemetry: z.record(z.unknown()).optional().nullable(),
  channel: z.string().optional().nullable(),
  created_at: z.string().optional().nullable(),
});

const BotRegisterSchema = z
  .object({
    handler: z.literal('bot'),
    type: z.literal('register'),
    data: BotRegisterDataSchema,
  })
  .passthrough();

const BotListSchema = z.object({
  type: z.literal('list'),
  data: z.array(BotSummarySchema),
});

const BotListRequestSchema = z
  .object({
    handler: z.literal('bot'),
    type: z.literal('list'),
  })
  .passthrough();

const BotRegistrationSchema = z.object({
  type: z.literal('registration'),
  data: BotSummarySchema,
});

const OperatorReadySchema = z
  .object({
    handler: z.literal('operator'),
    type: z.literal('operator_ready'),
    message_id: UuidSchema.optional(),
    idempotency_key: z.string().min(1).optional(),
    session_id: UuidSchema,
    robot_id: UuidSchema.optional(),
  })
  .passthrough();

const AttemptTimeoutSchema = z
  .object({
    handler: z.literal('operator'),
    type: z.literal('attempt_timeout'),
    message_id: UuidSchema.optional(),
    idempotency_key: z.string().min(1).optional(),
    session_id: UuidSchema,
    attempt_id: UuidSchema,
    robot_id: UuidSchema.optional(),
  })
  .passthrough();

const AttemptReadySchema = z
  .object({
    type: z.union([z.literal('attempt_ready'), z.literal('attempt_wait')]),
    message_id: UuidSchema.optional(),
    idempotency_key: z.string().min(1).optional(),
    session_id: UuidSchema,
    reason: z.string().optional(),
    handler: z.string().optional(),
  })
  .passthrough();

export const AttemptStartSchema = z
  .object({
    type: z.union([z.literal('attempt_start'), z.literal('attempt_started')]),
    message_id: UuidSchema.optional(),
    idempotency_key: z.string().min(1).optional(),
    session_id: UuidSchema,
    attempt_id: UuidSchema.optional(),
    attempt_no: z.number().optional(),
    duration_seconds: z.union([z.number(), z.string()]).optional(),
    duration: z.union([z.number(), z.string()]).optional(),
    seconds: z.union([z.number(), z.string()]).optional(),
    robot_id: UuidSchema.nullable().optional(),
    robot_name: z.string().nullable().optional(),
    handler: z.string().optional(),
  })
  .passthrough();

const AttemptResultSchema = z
  .object({
    type: z.literal('attempt_result'),
    message_id: UuidSchema.optional(),
    idempotency_key: z.string().min(1).optional(),
    session_id: UuidSchema.nullable().optional(),
    attempt_id: UuidSchema.nullable().optional(),
    success: z.union([z.boolean(), z.string()]).optional(),
    reason: z.string().optional(),
    robot_id: UuidSchema.optional(),
    robot_name: z.string().nullable().optional(),
    timestamp: z.string().optional(),
    handler: z.string().optional(),
  })
  .passthrough();

const SessionEventSchema = z
  .object({
    type: z.literal('session_event'),
    message_id: UuidSchema.optional(),
    idempotency_key: z.string().min(1).optional(),
    session_id: UuidSchema,
    event_type: z.string().optional(),
    stream: z.string().optional().nullable(),
    stream_id: z.string().optional().nullable(),
    payload: z
      .object({
        attempt_id: UuidSchema.optional(),
        ts: z.string().optional(),
      })
      .passthrough()
      .optional(),
    handler: z.string().optional(),
  })
  .passthrough();

const TeleopStatusSchema = z
  .object({
    type: z.literal('teleop_status'),
    handler: z.string().optional(),
    teleop_status: z.record(z.unknown()).optional(),
  })
  .passthrough();

export const ControlMessageSchema = z.union([
  AuthStatusSchema,
  AuthErrorSchema,
  WsPongSchema,
  BotRegisterSchema,
  BotListSchema,
  BotListRequestSchema,
  BotRegistrationSchema,
  OperatorReadySchema,
  AttemptTimeoutSchema,
  AttemptReadySchema,
  AttemptStartSchema,
  AttemptResultSchema,
  SessionEventSchema,
  TeleopStatusSchema,
]);

export const ControlClientMessageSchema = z.union([
  AuthClientSchema,
  WsPingClientSchema,
  MotionIntentClientSchema,
  GrabCommandSchema,
  BotRegisterSchema,
  BotListRequestSchema,
  OperatorReadySchema,
  AttemptTimeoutSchema,
]);

export type ControlMessage = z.infer<typeof ControlMessageSchema>;
export type ControlClientMessage = z.infer<typeof ControlClientMessageSchema>;

export const parseControlMessage = (raw: unknown): Result<ControlMessage, ContractError> => {
  const parsed = ControlMessageSchema.safeParse(raw);
  if (!parsed.success) {
    return {
      ok: false,
      err: toContractError(parsed.error, { source: 'control-ws' }),
    };
  }
  return { ok: true, value: parsed.data };
};
